import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "../api";
import { translations } from "../i18n";

const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);

export const SiteProvider = ({ children }) => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [lang, setLangState] = useState(() => localStorage.getItem("js_lang") || "es");

    const setLang = (l) => {
        localStorage.setItem("js_lang", l);
        setLangState(l);
        document.documentElement.lang = l;
    };

    useEffect(() => {
        document.documentElement.lang = lang;
    }, [lang]);

    const t = translations[lang] || translations.es;
    const cf = (field) => (lang === "en" && content?.[`${field}_en`] ? content[`${field}_en`] : content?.[field]);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/content");
            setContent(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!getToken()) return;
        api.get("/admin/me")
            .then(() => setIsAdmin(true))
            .catch(() => setToken(null));
    }, []);

    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            (res) => res,
            (error) => {
                if (error?.response?.status === 401) {
                    setToken(null);
                    setIsAdmin(false);
                }
                return Promise.reject(error);
            }
        );
        return () => api.interceptors.response.eject(interceptor);
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/admin/login", { email, password });
        setToken(data.token);
        setIsAdmin(true);
        return data;
    };

    const logout = () => {
        setToken(null);
        setIsAdmin(false);
        setAdminOpen(false);
    };

    return (
        <SiteContext.Provider
            value={{ content, loading, refresh, isAdmin, setIsAdmin, login, logout, adminOpen, setAdminOpen, lang, setLang, t, cf }}
        >
            {children}
        </SiteContext.Provider>
    );
};
